import { ConceptActivation, isConceptActivation } from '../../concept/ConceptActivation'
import { ValidityDeterminate } from '@fusion-advantage/backcompat'
import {
	ASTNode,
	ProgramNode,
	AssignmentStatementNode,
	IfStatementNode,
	PrintStatementNode,
	ReturnStatementNode,
	StatementNode,
	ExpressionNode,
	NodeType,
	ActivationReturnStatementNode,
	ValidationReturnStatementNode,
	ValueReturnStatementNode,
	ArrayLiteralNode,
	ForLoopNode,
	EmptyReturnStatementNode,
	ContinueStatementNode,
	DynamicFunctionNode,
} from './AstNode'
import { CalcScriptCompileEnv } from './CompileEnv'
import { CommonResponses, isCommonResponse } from './CommonResponses'
import { CoreFunctions, Keywords, Literals, Operators, Punctuation, ReturnHelpers, ScalarToken, ArrayToken, Token, TokenType, ValidationKeywords, createEOFToken } from './Token'
import { err, ok, Result } from 'neverthrow'
import { CalcType, CalcScriptCompileError, CalcScriptCompileErrorMsgs } from './CompilerTypes'
import { helperFunctions } from '../functions/HelperFunctions'

interface CalcScriptParseOk {
	programNode: ProgramNode
	calcType: CalcType
}

export class CalcScriptParser {
	private isUsed = false
	private tokens: Token[]
	// null until referred by return statement
	private calcType: CalcType|null = null
	private currentTokenIndex: number = 0
	private environment: CalcScriptCompileEnv

	constructor(tokens: Token[], environment: CalcScriptCompileEnv) {
		this.tokens = tokens
		this.environment = environment
	}

	public parse(): Result<CalcScriptParseOk, CalcScriptCompileError> {
		if(this.isUsed) {
			return err({
				area: 'CalcScriptParser',
				message: 'Attempted to reuse instance',
			})
		}
		this.isUsed = true
		const r = this.parseStatementList(true).map(({ statements }) => ({
			type: NodeType.Program,
			line: 0,
			column: 0,
			body: statements,
		}))
		if(r.isErr()) {
			return err(r.error)
		}
		if(!this.calcType) {
			return err({
				area: 'CalcScriptParser',
				message: CalcScriptCompileErrorMsgs.CalcTypeNotInferredByReturn(),
			})
		}
		return ok({
			programNode: r.value,
			calcType: this.calcType,
		})
	}

	private peekNextToken(): Token | null {
		const nextIndex = this.currentTokenIndex + 1
		if (nextIndex < this.tokens.length) {
			return this.tokens[nextIndex]
		}
		return null
	}

	private parseStatement(): Result<StatementNode, CalcScriptCompileError> {
		const token = this.currentToken()

		if (token.type === TokenType.IDENTIFIER) {
			this.environment.getScopeVariable(token.value)
			const nextToken = this.peekNextToken()
			if (nextToken && nextToken.value === Operators.ASSIGN) {
				return this.parseVariableAssignment()
			}
		}

		switch (token.value) {
			case Keywords.LET:      return this.parseAssignment()
			case Keywords.CONTINUE: return this.parseContinue()
			case CoreFunctions.FOR:     return this.parseForLoop()
			case CoreFunctions.IF:      return this.parseIfStatement()
			case Keywords.PRINT:    return this.parsePrintStatement()
			case Keywords.RETURN:   return this.parseReturnStatement()
			default:
				return err({
					area: 'CalcScriptParser',
					message: CalcScriptCompileErrorMsgs.UnexpectedStatement(token.value),
					line: token.line,
					column: token.column,
				})
		}
	}

	private parseForLoop(): Result<ForLoopNode, CalcScriptCompileError> {
		const forTokenResult = this.consume(TokenType.FUNCTION, CoreFunctions.FOR)
		if (forTokenResult.isErr()) return err(forTokenResult.error)
		const tkFor = forTokenResult.value

		const openParenResult = this.consume(TokenType.PUNCTUATION, Punctuation.OPEN_PAREN)
		if (openParenResult.isErr()) return err(openParenResult.error)

		const loopVariableResult = this.consume(TokenType.IDENTIFIER)
		if (loopVariableResult.isErr()) return err(loopVariableResult.error)

		const inResult = this.consume(TokenType.KEYWORD, Keywords.IN)
		if (inResult.isErr()) return err(inResult.error)

		const collectionTokenResult = this.consume(TokenType.IDENTIFIER)
		if (collectionTokenResult.isErr()) return err(collectionTokenResult.error)
		const tkCollection = collectionTokenResult.value

		const rGetCollectionExpr = this.environment.getScopeExpression(tkCollection.value)
		if (rGetCollectionExpr.isErr()) {
			return err({
				area: 'CalcScriptParser',
				message: rGetCollectionExpr.error,
				line: tkCollection.line,
				column: tkCollection.column,
			})
		}
		const collection = rGetCollectionExpr.value

		if (collection.type !== NodeType.Array && !helperFunctions.some(f => f.nodeType === collection.type)) {
			return err({
				area: 'CalcScriptParser',
				message: `Expected array or function call for FOR loop collection type, got ${collection.type}`,
				line: tkCollection.line,
				column: tkCollection.column,
			})
		}

		const closeParenResult = this.consume(TokenType.PUNCTUATION, Punctuation.CLOSE_PAREN)
		if (closeParenResult.isErr()) return err(closeParenResult.error)

		const loopBodyResult = this.parseBlock([loopVariableResult.value.value])
		if (loopBodyResult.isErr()) return err(loopBodyResult.error)

		return ok({
			type: NodeType.ForLoop,
			line: tkFor.line,
			column: tkFor.column,
			collection,
			loopVariable: loopVariableResult.value.value,
			loopBody: loopBodyResult.value.statements,
			hasReturn: loopBodyResult.value.hasReturn,
		})
	}

	private parseIfStatement(): Result<IfStatementNode, CalcScriptCompileError> {
		const ifTokenResult = this.consume(TokenType.FUNCTION, CoreFunctions.IF)
		if (ifTokenResult.isErr()) return err(ifTokenResult.error)
		const tkIf = ifTokenResult.value

		const conditionResult = this.parseExpression()
		if (conditionResult.isErr()) return err(conditionResult.error)

		const consequentBlockResult = this.parseBlock()
		if (consequentBlockResult.isErr()) return err(consequentBlockResult.error)
		const consequentBlock = consequentBlockResult.value

		let alternate: ASTNode[] | IfStatementNode | null = null
		let allBranchesHaveReturn = consequentBlock.hasReturn
		let guaranteedReturn = false

		if (this.currentToken().value === CoreFunctions.ELSE) {
			const elseResult = this.consume(TokenType.FUNCTION, CoreFunctions.ELSE)
			if (elseResult.isErr()) return err(elseResult.error)

			if (this.currentToken().value === CoreFunctions.IF) {
				const nestedIfResult = this.parseIfStatement()
				if (nestedIfResult.isErr()) return err(nestedIfResult.error)
				alternate = nestedIfResult.value
				allBranchesHaveReturn = allBranchesHaveReturn && (nestedIfResult.value.hasReturn ?? false)
			} else {
				const elseBlockResult = this.parseBlock()
				if (elseBlockResult.isErr()) return err(elseBlockResult.error)
				const elseBlock = elseBlockResult.value
				alternate = elseBlock.statements
				allBranchesHaveReturn = allBranchesHaveReturn && elseBlock.hasReturn
				guaranteedReturn = allBranchesHaveReturn
			}
		}

		return ok({
			type: NodeType.IfStatement,
			line: tkIf.line,
			column: tkIf.column,
			condition: conditionResult.value,
			consequent: consequentBlock.statements,
			alternate,
			hasReturn: allBranchesHaveReturn,
			guaranteedReturn,
		})
	}

	private parsePrintStatement(): Result<PrintStatementNode, CalcScriptCompileError> {
		const printTokenResult = this.consume(TokenType.KEYWORD, Keywords.PRINT)
		if (printTokenResult.isErr()) return err(printTokenResult.error)
		const tkPrint = printTokenResult.value

		const openParenResult = this.consume(TokenType.PUNCTUATION, Punctuation.OPEN_PAREN)
		if (openParenResult.isErr()) return err(openParenResult.error)

		const argumentResult = this.parseExpression()
		if (argumentResult.isErr()) return err(argumentResult.error)

		const closeParenResult = this.consume(TokenType.PUNCTUATION, Punctuation.CLOSE_PAREN)
		if (closeParenResult.isErr()) return err(closeParenResult.error)

		return ok({
			type: NodeType.PrintStatement,
			line: tkPrint.line,
			column: tkPrint.column,
			argument: argumentResult.value,
		})
	}

	private parseReturnStatement(): Result<ReturnStatementNode | IfStatementNode, CalcScriptCompileError> {
		const returnTokenResult = this.consume(TokenType.KEYWORD, Keywords.RETURN)
		if (returnTokenResult.isErr()) return err(returnTokenResult.error)
		const returnToken = returnTokenResult.value
		const nextToken = this.currentToken()

		if (nextToken.type === TokenType.FUNCTION && nextToken.value === ReturnHelpers.ACTIVE_WHEN) {
			return this.parseActiveWhen(returnToken)
		} else if (nextToken.type === TokenType.FUNCTION && nextToken.value === ReturnHelpers.REQUIRED_WHEN) {
			return this.parseRequiredWhen(returnToken)
		} else if (nextToken.type === TokenType.VALIDATION_KEYWORD) {
			return this.parseValidationReturnStatement(returnToken)
		} else if (nextToken.type === TokenType.ACTIVATION_KEYWORD) {
			return this.parseActivationReturnStatement(returnToken)
		} else if (nextToken.line !== returnToken.line || nextToken.type === 'EOF') {
			return this.parseEmptyReturnStatement(returnToken)
		} else {
			return this.parseValueReturnStatement(returnToken)
		}
	}

	private inferCalcTypeWithReturn(newCalcType: CalcType, token: ScalarToken): Result<void, CalcScriptCompileError> {
		if(this.calcType && this.calcType !== newCalcType) {
			return err({
				area: 'CalcScriptParser',
				message: CalcScriptCompileErrorMsgs.ReturnStatementInferredChangedCalcType(this.calcType, newCalcType),
				line: token.line,
				column: token.column,
			})
		}
		this.calcType = newCalcType
		return ok()
	}

	// an empty return statement can be used to exit out of a value calc script
	// without setting a new current value
	private parseEmptyReturnStatement(returnToken: ScalarToken): Result<EmptyReturnStatementNode, CalcScriptCompileError> {
		const rInferType = this.inferCalcTypeWithReturn(CalcType.Value, returnToken)
		if(rInferType.isErr()) {
			return err(rInferType.error)
		}
		return ok({
			type: NodeType.EmptyReturnStatement,
			line: returnToken.line,
			column: returnToken.column,
		})
	}

	private parseValueReturnStatement(returnToken: ScalarToken): Result<ValueReturnStatementNode, CalcScriptCompileError> {
		const rInferType = this.inferCalcTypeWithReturn(CalcType.Value, returnToken)
		if(rInferType.isErr()) {
			return err(rInferType.error)
		}

		const valueResult = this.parseExpression()
		if (valueResult.isErr()) return err(valueResult.error)

		let subconceptId: ExpressionNode | undefined
		if (this.currentToken().type === TokenType.FUNCTION && this.currentToken().value === CoreFunctions.FOR) {
			const forResult = this.consume(TokenType.FUNCTION, CoreFunctions.FOR)
			if (forResult.isErr()) return err(forResult.error)

			const subconceptResult = this.parseExpression()
			if (subconceptResult.isErr()) return err(subconceptResult.error)
			subconceptId = subconceptResult.value
		}

		return ok({
			type: NodeType.ValueReturnStatement,
			line: returnToken.line,
			column: returnToken.column,
			value: valueResult.value,
			subconceptId,
		})
	}

	private parseValidationReturnStatement(returnToken: ScalarToken): Result<ValidationReturnStatementNode, CalcScriptCompileError> {
		const rInferType = this.inferCalcTypeWithReturn(CalcType.Validation, returnToken)
		if(rInferType.isErr()) {
			return err(rInferType.error)
		}

		const validityTokenResult = this.consume(TokenType.VALIDATION_KEYWORD)
		if (validityTokenResult.isErr()) return err(validityTokenResult.error)
		const validityToken = validityTokenResult.value

		let message: string | undefined

		if (this.currentToken().type === TokenType.PUNCTUATION && this.currentToken().value === Punctuation.OPEN_PAREN) {
			const openResult = this.consume(TokenType.PUNCTUATION, Punctuation.OPEN_PAREN)
			if (openResult.isErr()) return err(openResult.error)

			const messageTokenResult = this.consume(TokenType.STRING)
			if (messageTokenResult.isErr()) return err(messageTokenResult.error)
			message = messageTokenResult.value.value

			const closeResult = this.consume(TokenType.PUNCTUATION, Punctuation.CLOSE_PAREN)
			if (closeResult.isErr()) return err(closeResult.error)
		}

		let validity = ValidityDeterminate.Valid
		switch (validityToken.value) {
			case ValidationKeywords.ABNORMAL:    validity = ValidityDeterminate.Abnormal;   break
			case ValidationKeywords.INVALID:     validity = ValidityDeterminate.Invalid;    break
			case ValidationKeywords.OUT_OF_RANGE: validity = ValidityDeterminate.OutOfRange; break
			case ValidationKeywords.REQUIRED:    validity = ValidityDeterminate.Required;   break
		}

		return ok({
			type: NodeType.ValidationReturnStatement,
			line: returnToken.line,
			column: returnToken.column,
			validity,
			message,
		})
	}

	private parseActivationReturnStatement(returnToken: ScalarToken): Result<ActivationReturnStatementNode, CalcScriptCompileError> {
		const rInferType = this.inferCalcTypeWithReturn(CalcType.Activation, returnToken)
		if(rInferType.isErr()) {
			return err(rInferType.error)
		}
		const activationTokenResult = this.consume(TokenType.ACTIVATION_KEYWORD)
		if (activationTokenResult.isErr()) return err(activationTokenResult.error)

		const value = String(activationTokenResult.value.value).toLowerCase()

		if (!isConceptActivation(value)) {
			return err({
				area: 'CalcScriptParser',
				message: `Invalid activation value '${returnToken.value}'`,
				line: returnToken.line,
				column: returnToken.column,
			})
		}

		return ok({
			type: NodeType.ActivationReturnStatement,
			line: returnToken.line,
			column: returnToken.column,
			activation: value,
		})
	}

	private parseReturnHelperArguments(keyword: string, expectedCount: number): Result<ExpressionNode[], CalcScriptCompileError> {
		const tokenResult = this.consume(TokenType.FUNCTION, keyword)
		if (tokenResult.isErr()) return err(tokenResult.error)
		const token = tokenResult.value

		const argsResult = this.parseArguments()
		if (argsResult.isErr()) return err(argsResult.error)
		if (argsResult.value.length !== expectedCount) {
			return err({
				area: 'CalcScriptParser',
				message: `${keyword} expects ${expectedCount} argument(s) but got ${argsResult.value.length}`,
				line: token.line,
				column: token.column,
			})
		}
		return ok(argsResult.value)
	}

	// RETURN ACTIVE_WHEN(condition) expands to:
	//   IF (condition) { RETURN ACTIVE } ELSE { RETURN DISABLED }
	private parseActiveWhen(returnToken: ScalarToken): Result<IfStatementNode, CalcScriptCompileError> {
		const rInferType = this.inferCalcTypeWithReturn(CalcType.Activation, returnToken)
		if (rInferType.isErr()) return err(rInferType.error)

		const argsResult = this.parseReturnHelperArguments(ReturnHelpers.ACTIVE_WHEN, 1)
		if (argsResult.isErr()) return err(argsResult.error)
		const [condition] = argsResult.value

		const at = { line: returnToken.line, column: returnToken.column }
		return ok({
			type: NodeType.IfStatement,
			...at,
			condition,
			consequent: [{ type: NodeType.ActivationReturnStatement, ...at, activation: ConceptActivation.Active }],
			alternate: [{ type: NodeType.ActivationReturnStatement, ...at, activation: ConceptActivation.Disabled }],
			hasReturn: true,
			guaranteedReturn: true,
		})
	}

	// RETURN REQUIRED_WHEN(condition, cid) expands to:
	//   IF (HAS_ANY_VALUE(cid)) { RETURN VALID }
	//   ELSE IF (condition) { RETURN REQUIRED }
	//   ELSE { RETURN VALID }
	private parseRequiredWhen(returnToken: ScalarToken): Result<IfStatementNode, CalcScriptCompileError> {
		const rInferType = this.inferCalcTypeWithReturn(CalcType.Validation, returnToken)
		if (rInferType.isErr()) return err(rInferType.error)

		const argsResult = this.parseReturnHelperArguments(ReturnHelpers.REQUIRED_WHEN, 2)
		if (argsResult.isErr()) return err(argsResult.error)
		const [condition, cid] = argsResult.value

		const at = { line: returnToken.line, column: returnToken.column }
		const validReturn = (): ValidationReturnStatementNode => ({ type: NodeType.ValidationReturnStatement, ...at, validity: ValidityDeterminate.Valid })
		return ok({
			type: NodeType.IfStatement,
			...at,
			condition: { type: NodeType.HasAnyValue, ...at, arguments: [cid] },
			consequent: [validReturn()],
			alternate: {
				type: NodeType.IfStatement,
				...at,
				condition,
				consequent: [{ type: NodeType.ValidationReturnStatement, ...at, validity: ValidityDeterminate.Required }],
				alternate: [validReturn()],
				hasReturn: true,
				guaranteedReturn: true,
			},
			hasReturn: true,
			guaranteedReturn: true,
		})
	}

	private parseVariableAssignment(): Result<AssignmentStatementNode, CalcScriptCompileError> {
		const identifierTokenResult = this.consume(TokenType.IDENTIFIER)
		if (identifierTokenResult.isErr()) return err(identifierTokenResult.error)
		const tkId = identifierTokenResult.value

		const assignResult = this.consume(TokenType.OPERATOR, Operators.ASSIGN)
		if (assignResult.isErr()) return err(assignResult.error)

		const expressionResult = this.parseExpression()
		if (expressionResult.isErr()) return err(expressionResult.error)

		const setResult = this.environment.setVariable(tkId.value, expressionResult.value)
		if (setResult.isErr()) {
			return err({
				area: 'CalcScriptParser',
				message: setResult.error,
				line: tkId.line,
				column: tkId.column,
			})
		}

		return ok({
			type: NodeType.AssignmentStatement,
			line: tkId.line,
			column: tkId.column,
			identifier: tkId.value,
			expression: expressionResult.value,
		})
	}

	private parseExpression(precedence: number = 0): Result<ExpressionNode, CalcScriptCompileError> {
		const leftResult = this.parsePrimary()
		if (leftResult.isErr()) return err(leftResult.error)
		let left = leftResult.value

		while (true) {
			const tokenPrecedence = this.getPrecedence()
			if (tokenPrecedence > precedence) {
				const operatorTokenResult = this.consume([TokenType.OPERATOR, TokenType.LOGICAL_OPERATOR])
				if (operatorTokenResult.isErr()) return err(operatorTokenResult.error)
				const tkOperator = operatorTokenResult.value

				const rightResult = this.parseExpression(tokenPrecedence)
				if (rightResult.isErr()) return err(rightResult.error)

				left = {
					type: NodeType.BinaryExpression,
					line: tkOperator.line,
					column: tkOperator.column,
					operator: tkOperator.value,
					left,
					right: rightResult.value,
				}
			} else {
				break
			}
		}

		return ok(left)
	}

	private parseArguments(): Result<ExpressionNode[], CalcScriptCompileError> {
		const openParenResult = this.consume(TokenType.PUNCTUATION, Punctuation.OPEN_PAREN)
		if (openParenResult.isErr()) return err(openParenResult.error)

		const args: ExpressionNode[] = []

		if (this.currentToken().type !== TokenType.PUNCTUATION || this.currentToken().value !== Punctuation.CLOSE_PAREN) {
			do {
				const argResult = this.parseExpression()
				if (argResult.isErr()) return err(argResult.error)
				args.push(argResult.value)
			} while (
				this.currentToken().type === TokenType.PUNCTUATION &&
				this.currentToken().value === Punctuation.COMMA &&
				this.consume(TokenType.PUNCTUATION, Punctuation.COMMA).isOk()
			)
		}

		const closeParenResult = this.consume(TokenType.PUNCTUATION, Punctuation.CLOSE_PAREN)
		if (closeParenResult.isErr()) return err(closeParenResult.error)

		return ok(args)
	}

	private parseAssignment(): Result<AssignmentStatementNode, CalcScriptCompileError> {
		const letTokenResult = this.consume(TokenType.KEYWORD, Keywords.LET)
		if (letTokenResult.isErr()) return err(letTokenResult.error)
		const tkLet = letTokenResult.value

		const identifierTokenResult = this.consume(TokenType.IDENTIFIER)
		if (identifierTokenResult.isErr()) return err(identifierTokenResult.error)

		const assignResult = this.consume(TokenType.OPERATOR, Operators.ASSIGN)
		if (assignResult.isErr()) return err(assignResult.error)

		const expressionResult = this.parseExpression()
		if (expressionResult.isErr()) return err(expressionResult.error)

		const declareResult = this.environment.declareVariable(identifierTokenResult.value.value, expressionResult.value)
		if (declareResult.isErr()) {
			return err({
				area: 'CalcScriptParser',
				message: declareResult.error,
				line: tkLet.line,
				column: tkLet.column,
			})
		}

		return ok({
			type: NodeType.AssignmentStatement,
			line: tkLet.line,
			column: tkLet.column,
			identifier: identifierTokenResult.value.value,
			expression: expressionResult.value,
		})
	}

	private parseContinue(): Result<ContinueStatementNode, CalcScriptCompileError> {
		const continueTokenResult = this.consume(TokenType.KEYWORD, Keywords.CONTINUE)
		if (continueTokenResult.isErr()) return err(continueTokenResult.error)
		const tkContinue = continueTokenResult.value

		return ok({
			type: NodeType.ContinueStatement,
			line: tkContinue.line,
			column: tkContinue.column,
		})
	}

	private parseArrayExpression(token: ArrayToken): Result<ArrayLiteralNode, CalcScriptCompileError> {
		const nodeArr: ExpressionNode[] = []

		for (const childToken of token.value) {
			if (childToken.type === TokenType.ARRAY) {
				return err({
					area: 'CalcScriptParser',
					message: CalcScriptCompileErrorMsgs.MultiDimensionalArray(),
					line: childToken.line,
					column: childToken.column,
				})
			}
			const nodeResult = this.parsePrimary(childToken)
			if (nodeResult.isErr()) return err(nodeResult.error)
			nodeArr.push(nodeResult.value)
		}

		return ok({
			type: NodeType.Array,
			line: token.line,
			column: token.column,
			elements: nodeArr,
		})
	}

	private getPrecedence(): number {
		const token = this.currentToken()
		const precedences: { [key: string]: number } = {
			'OR': 1, 'AND': 2,
			'==': 3, '!=': 3,
			'<': 4, '<=': 4, '>': 4, '>=': 4,
			'+': 5, '-': 5,
			'*': 6, '/': 6,
		}
		return typeof token.value === 'string' ? (precedences[token.value] || 0) : 0
	}

	private parsePrimary(tokenArg?: Token): Result<ExpressionNode, CalcScriptCompileError> {
		const token = tokenArg ?? this.currentToken()
		const isChildToken = !!tokenArg

		switch (token.type) {
			case TokenType.NUMBER:
				!isChildToken && this.consume(token.type)
				return ok({
					type: NodeType.NumericLiteral,
					line: token.line,
					column: token.column,
					value: Number(token.value),
				})

			case TokenType.STRING:
				!isChildToken && this.consume(token.type)
				return ok({
					type: NodeType.StringLiteral,
					line: token.line,
					column: token.column,
					value: token.value,
				})

			case TokenType.ARRAY:
				!isChildToken && this.advance()
				return this.parseArrayExpression(token)

			case TokenType.IDENTIFIER: {
				const identifier = token.value
				const rGet = this.environment.getScopeVariable(identifier)
				if (rGet.isErr()) {
					return err({
						area: 'CalcScriptParser',
						message: rGet.error,
						line: token.line,
						column: token.column,
					})
				}
				!isChildToken && this.consume(token.type)
				return ok({
					type: NodeType.Identifier,
					line: token.line,
					column: token.column,
					name: identifier,
				})
			}

			case TokenType.PUNCTUATION:
				if (token.value === Punctuation.OPEN_PAREN) {
					if (!isChildToken) {
						const openResult = this.consume(token.type, Punctuation.OPEN_PAREN)
						if (openResult.isErr()) return err(openResult.error)
					}
					const exprResult = this.parseExpression()
					if (exprResult.isErr()) return err(exprResult.error)
					if (!isChildToken) {
						const closeResult = this.consume(token.type, Punctuation.CLOSE_PAREN)
						if (closeResult.isErr()) return err(closeResult.error)
					}
					return ok(exprResult.value)
				}
				break

			case TokenType.LITERAL:
				if (token.value === Literals.TRUE || token.value === Literals.FALSE) {
					!isChildToken && this.consume(TokenType.LITERAL, [Literals.TRUE, Literals.FALSE])
					return ok({
						type: NodeType.BooleanLiteral,
						line: token.line,
						column: token.column,
						value: token.value === Literals.TRUE,
					})
				} else if (token.value === Literals.NULL) {
					!isChildToken && this.consume(TokenType.LITERAL, Literals.NULL)
					return ok({
						type: NodeType.NullLiteral,
						line: token.line,
						column: token.column,
						value: null,
					})
				}
				break

			case TokenType.CONSTANT:
				!isChildToken && this.consume(token.type)
				if (isCommonResponse(token.value)) {
					return ok({
						type: NodeType.StringLiteral,
						line: token.line,
						column: token.column,
						value: `"${CommonResponses[token.value]}"`,
					})
				}
				return ok({
					type: NodeType.Constant,
					line: token.line,
					column: token.column,
					name: token.value,
				})

			case TokenType.FUNCTION: {
				const helperFunction = helperFunctions.find(h => h.tokenName === token.value)
				if (helperFunction) {
					return this.parseDynamicFunction(helperFunction.nodeType, token.value)
				}
			}
		}

		return err({
			area: 'CalcScriptParser',
			message: `Unexpected expression ${token.type} with value '${token.value}'`,
			line: token.line,
			column: token.column,
		})
	}

	private parseStatementList(requireReturn: boolean = false): Result<{ statements: ASTNode[], hasReturn: boolean }, CalcScriptCompileError> {
		const statements: ASTNode[] = []
		const returnStatementTypes: string[] = [
			NodeType.ValueReturnStatement,
			NodeType.ValidationReturnStatement,
			NodeType.ActivationReturnStatement,
			NodeType.EmptyReturnStatement
		]
		let hasReturn = false

		while (
			this.currentToken() &&
			this.currentToken().type !== 'EOF' &&
			this.currentToken().value !== Punctuation.CLOSE_BRACE
		) {
			const statementResult = this.parseStatement()
			if (statementResult.isErr()) return err(statementResult.error)

			const statement = statementResult.value
			statements.push(statement)

			if (returnStatementTypes.includes(statement.type)) {
				hasReturn = true
				break
			} else if (statement.type === NodeType.IfStatement) {
				hasReturn = statement.guaranteedReturn
			} else if (statement.hasReturn && statement.type !== NodeType.ForLoop) {
				hasReturn = true
			}
		}

		if (requireReturn && !hasReturn) {
			// not enforced — does not stop calc from running correctly and floods the message array
		}

		return ok({ statements, hasReturn })
	}

	private parseBlock(dependentVars?: string[]): Result<{ statements: ASTNode[], hasReturn: boolean }, CalcScriptCompileError> {
		const openBraceResult = this.consume(TokenType.PUNCTUATION, Punctuation.OPEN_BRACE)
		if (openBraceResult.isErr()) return err(openBraceResult.error)
		const tkOpen = openBraceResult.value

		this.environment.enterScope()

		if (dependentVars) {
			for (const dependentVar of dependentVars) {
				const declareResult = this.environment.declareVariable(dependentVar)
				if (declareResult.isErr()) {
					this.environment.exitScope()
					return err({
						area: 'CalcScriptParser',
						message: 'Error declaring dependent var: ' + declareResult.error,
						line: tkOpen.line,
						column: tkOpen.column,
					})
				}
			}
		}

		const statementListResult = this.parseStatementList()

		this.environment.exitScope()

		if (statementListResult.isErr()) return err(statementListResult.error)

		if (this.currentToken().value === Punctuation.CLOSE_BRACE) {
			const closeBraceResult = this.consume(TokenType.PUNCTUATION, Punctuation.CLOSE_BRACE)
			if (closeBraceResult.isErr()) return err(closeBraceResult.error)
		}

		return statementListResult
	}

	private parseDynamicFunction(type: DynamicFunctionNode['type'], keyword: string): Result<DynamicFunctionNode, CalcScriptCompileError> {
		const tokenResult = this.consume(TokenType.FUNCTION, keyword)
		if (tokenResult.isErr()) return err(tokenResult.error)
		const token = tokenResult.value

		const argsResult = this.parseArguments()
		if (argsResult.isErr()) return err(argsResult.error)

		return ok({
			type,
			line: token.line,
			column: token.column,
			arguments: argsResult.value,
		})
	}

	private currentToken(): Token {
		const lastToken = this.tokens[this.tokens.length - 1]
		return this.tokens[this.currentTokenIndex] || createEOFToken(lastToken)
	}

	private advance(): void {
		this.currentTokenIndex++
	}

	private consume(expectedTypes: ScalarToken['type'] | ScalarToken['type'][], expectedValues: string | string[] = []): Result<ScalarToken, CalcScriptCompileError> {
		const token = this.currentToken()
		const expectedTypesArray = Array.isArray(expectedTypes) ? expectedTypes : [expectedTypes]
		const expectedValuesArray = Array.isArray(expectedValues) ? expectedValues : [expectedValues]

		if (token.type === TokenType.ARRAY) {
			const expTypeList = expectedTypesArray.join('|')
			return err({
				area: 'CalcScriptParser',
				message: `Expected scalar token type ${expTypeList} but got ${TokenType.ARRAY}`,
				line: token.line,
				column: token.column,
			})
		}

		if (expectedTypesArray.includes(token.type) && (expectedValuesArray.length === 0 || expectedValuesArray.includes(token.value))) {
			this.currentTokenIndex++
			return ok(token)
		}

		const expTypeList = expectedTypesArray.join('|')
		const expValList = expectedValuesArray.map(v => `'${v}'`).join('|')
		return err({
			area: 'CalcScriptParser',
			message: `Expected token type ${expTypeList} with value ${expValList} but got ${token.type} with value '${token.value}`,
			line: token.line,
			column: token.column,
		})
	}
}